import { Container, Sprite } from "pixi.js";
import type { Texture } from "pixi.js";
import type { FountainCoin } from "./CoinFountainView";

/** The narrowest a turning coin gets, a share of its width: edge on, its rim still shows. */
const EDGE = 0.08;

/**
 * A fountain coin drawn by one still image, turning about its upright axis as it flies: its width follows the turn's
 * cosine (its back the same art, mirrored), one whole turn every `turnSeconds` at a rate of 1, from its own phase, so no
 * two coins turn in step. The fountain moves it on (`advance`) on its own runner each frame it flies; in the pool it
 * keeps still. For a title whose coin is an image (slot-coin-hold's); a coin whose spin is a Spine clip is `SpineCoin`.
 */
export class SpriteCoin implements FountainCoin {
  public readonly node: Container;
  private readonly face: Sprite;
  private readonly turnSeconds: number;
  /** The face's horizontal scale at its full width. */
  private readonly faceScaleX: number;
  private rate = 1;
  /** Where in its turn it is, 0–1. */
  private turn = 0;

  public constructor(input: {
    texture: Texture;
    /** Its width, at the fountain's coin scale 1 (its height keeps the image's proportions). */
    size: number;
    /** One whole turn at a rate of 1, in seconds of the fountain's runner. */
    turnSeconds: number;
  }) {
    this.node = new Container();
    this.face = new Sprite({ texture: input.texture, anchor: 0.5 });
    this.face.setSize(input.size, (input.size * input.texture.height) / Math.max(1, input.texture.width));
    this.faceScaleX = this.face.scale.x;
    this.node.addChild(this.face);
    this.turnSeconds = input.turnSeconds;
  }

  public startSpin(rate: number, phase: number): void {
    this.rate = rate;
    this.turn = phase;
    this.show();
  }

  public stopSpin(): void {
    // Nothing ticks it in the pool: the fountain advances only the coins in flight.
  }

  public advance(seconds: number): void {
    if (this.turnSeconds <= 0) return;
    this.turn = (this.turn + (seconds * this.rate) / this.turnSeconds) % 1;
    this.show();
  }

  private show(): void {
    const width = Math.cos(2 * Math.PI * this.turn);
    const shown = Math.abs(width) < EDGE ? (width < 0 ? -EDGE : EDGE) : width;
    this.face.scale.x = this.faceScaleX * shown;
  }
}
